import { Location } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivationEnd, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { AuthService } from '@app/services/auth/auth.service';
import menuData from '@app/assets/menu.json';
import { IMenu } from '@app/interfaces/IMenu';
import { TourGuideService } from '@app/services/tour-guide.service';
import { MatDialog } from '@angular/material/dialog';
import { TourGuideContent } from '@app/util/tour-guide-content.util';
import { DropDownOption } from '@app/interfaces/IDropDown';
import { ConstantsService } from '@app/services/constants.service';
import { Labels } from '@app/util/labels.util';
import { WfQueueManagerService } from '@app/services/wf-queue-manager.service';
import { LoaderService } from '@app/interceptors/loaderInterceptor/loader.service';
import { CommonFunctionalitiesService } from '@app/services/common-functionalities.service';
import { Subscription } from 'rxjs';
import { WorkflowRunService } from '@app/services/workflow-run.service';
import { SummaryService } from '@app/services/summary.service';
import { I18nService } from '@app/services/i18n/i18n.service';
import { ConfigBuilderService } from '@app/services/config-builder.service';
import { AppFeatureService } from '@app/services/app-feature.service';
import { FormControl } from '@angular/forms';
import { Messages } from '@app/src/util/messages';
import jsondata from '@app/assets/extlable.json';
import { MatTableDataSource } from '@angular/material/table';
import { ToastService } from '@app/services/toast.service';
import { Messages as msg } from '@app/src/util/messages.util';
import { ExtLinksService } from '@app/services/external-links.service';
import { HttpErrorResponse } from '@angular/common/http';
import { GITHUB_WORKFLOWS_EXEC } from '@app/src/util/common-utils';
import { RouteStateService } from '@app/services/route-state.service';

/**
 * **********************************************************
 * Navbar component 
 * Displays horizontal menu
 * Loads the page content as per the menu selected
 * **********************************************************
 */

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss']

})
export class NavbarComponent implements OnInit, OnDestroy {
  //variables
  // this is for component we selected in navbar to show his name
  componentName: string = "";
  menuArray: IMenu[] = [];
  accessObj: any = {};
  wfTitle: string = Labels.workflows;
  durationTitle: string = Labels.duration;
  tourId = TourGuideContent
  repo: DropDownOption[] = [];
  pageNm: any[] = [];
  selectedRepo: DropDownOption = this.repo[0];
  durationDropdown: DropDownOption[] = this.constants.DURATION_DROPDOWN; // duration dropdown
  selectedDuration: DropDownOption = this.durationDropdown[0]; // default selected duration
  durationStyle = {
    'outline': 'auto',
    'background': 'none',
    'outline-color': '#959593'
  }
  hasResetDropdowns: any;
  subscriptions: Subscription = new Subscription();
  gitActionsResp: any;
  dummyFormControl: FormControl;
  links: any;
  extLinks: any;
  helpItem = this.constants.helpItems;
  helpButtonLink: Boolean;
  matTooltipHelp: any;
  noLinkMsg = Messages.noLink;
  options = jsondata;
  cicdLabel = 'OPTIMUS CICD Dashboard';
  
  // Pages that are open to every logged-in user regardless of role/page/feature access.
  private readonly openAccessPages: string[] = ['Hil Test Results Upload', 'Dashboard'];
  
  // Route state observables from centralized service
  isWorkflowSummaryView$ = this.routeStateService.isWorkflowSummaryView$;
  isPrerequisiteView$ = this.routeStateService.isWorkflowPrerequisiteView$;
  isWorkflowExecutionHistoryView$ = this.routeStateService.isWorkflowExecutionHistoryView$;

  // Getter properties for template access
  get isWorkflowSummaryView(): boolean {
    return this.routeStateService.isWorkflowSummaryView;
  }

  get isPrerequisiteView(): boolean {
    return this.routeStateService.isWorkflowPrerequisiteView;
  }

  get isWorkflowExecutionHistoryView(): boolean {
    return this.routeStateService.isWorkflowExecutionHistoryView;
  }

  /**
   * 
   * @param location 
   * @param authService 
   * @param router 
   * @param dialog 
   * @param helpService 
   * @param guidedTourService 
   * @param constants 
   * @param wfQueueService 
   * @param commonService 
   * @param loaderService 
   */
  constructor(private location: Location,
    public readonly authService: AuthService,
    public router: Router,
    public dialog: MatDialog,
    public guidedTourService: TourGuideService,
    private constants: ConstantsService,
    public wfQueueService: WfQueueManagerService,
    public commonService: CommonFunctionalitiesService,
    public loaderService: LoaderService,
    private wfRunService: WorkflowRunService,
    private summaryService: SummaryService,
    public il8Service: I18nService,
    private configBuilderService: ConfigBuilderService,
    private extLinksService: ExtLinksService,
    private appFeatService: AppFeatureService,
    private ts: ToastService,
    public readonly routeStateService: RouteStateService) {
    this.accessObj = {
      'Dashboard': false,
      'Feature Workflows': false,
      'Workflow Queue Manager': false,
      'Configuration Builder': false,
      'Integrated Workflows': false,
      'Workflows': false,
      'Pr Grouping': false,
      'Hil Test Results Upload': false,
      'Admin': false,
      'Tools': false,
      'Links': false,
      'Help': false,
      'Github Actions': false,
      'Github Sync': false,
      'Dynamic Configuration': false,
      'Labels': false,
      'Scheduler': false,
      'Job Executions': false
    };
    this.dummyFormControl = new FormControl();
  }

  /**
   * Lifecycle hook called when the component is initialized.
   * - Checks if the current route is '/proxy-login' and sets 'proxyLogin' in the authentication service accordingly.
   * - Loads the navigation menu based on user access permissions.
   * - Subscribes to router events to update the component name based on navigation changes.
  */
  ngOnInit(): void {
    if (this.authService.proxyLogin) {
      this.authService.checkPermissions();
    }

    // Load the navigation menu based on user access permissions
    this.loadNavbar();

    // Subscribe to router events to update the component name when navigation starts
    this.subscriptions.add(this.router.events.subscribe((event: any) => {

      // Check if the current route is '/proxy-login' and set 'proxyLogin' in the authentication service accordingly
      if (event instanceof NavigationEnd && this.router.url === '/proxy-login') {
        this.authService.proxyLogin = this.router.url === '/proxy-login';
      }

      if (event instanceof NavigationEnd && this.router.url === '/dashboard') {
        this.subscriptions.add(this.summaryService.selectedTabIndex$.subscribe((index) => {
          if (index === 1) {
            this.resetDropdowns();
          }
        }));
      }

      if (event instanceof NavigationEnd && !event.url.includes('/workflow-run')) {
        this.resetDropdowns();
      }

      if (event instanceof NavigationEnd && this.router.url.includes('/admin')) {
        this.resetDropdowns('admin');
      }

      if (event instanceof NavigationEnd && this.router.url.includes('/github-actions')) {
        this.getGithubActions();
        this.dummyFormControl.setValue('All');
      }

      if (event instanceof ActivationEnd && this.router.url.includes('/workflow-run')) {
        if (this.guidedTourService.isStartTour) {
          return
        }
        this.repo = [...this.repo];
        //Remove the dropdown value: All
        this.repo = this.repo.filter(element => element.name !== 'All');

        const value: any = this.repo.find((element: any) => event.snapshot.params['wfId'] == element.value)
        if (value?.active) {
          delete value.active
        }
        this.selectedRepo = value;
      }

      if (event instanceof NavigationStart) {
        let idx = this.repo.findIndex((el: any) => el.name === 'All')
        idx === -1 ? this.repo.unshift(this.constants.WF_DEFAULT_OPT[0]) : this.repo[idx] = this.constants.WF_DEFAULT_OPT[0]
        // Update the component name when a navigation event starts
        this.updateComponentName()
      }
    }));

    this.subscriptions.add(
      this.configBuilderService.triggerGetWfDetails$.subscribe((res: any) => {
        if (res) {
          this.getWorkRepoNames('configUpdate');
        }
      })
    );
    this.subscriptions.add(
      this.authService.getUserData$.subscribe((user) => {
        if (user || this.authService.proxyLogin) {
          this.getWorkRepoNames();
          this.getPageNm();
          this.getExternalLinksData();
        }
      })
    );
  }


  /**
   * Loads the navigation menu based on user access permissions and updates the 'menuArray'.
   * - Calls 'accessUpdated' to update access permissions.
   * - Filters 'menuData' based on access permissions and populates 'menuArray'.
   * - Calls 'updateComponentName' to update the component name.
   */
  async loadNavbar() {

    // Clear the 'menuArray' to start with an empty menu
    this.menuArray = []
    // Wait for access permissions to be updated
    await this.accessUpdated();

    // If the user is logged in or in proxy login mode, populate 'menuArray' based on access permissions
    if (this.authService.isLoggedIn() || this.authService.proxyLogin) {
      for (const menu of menuData) {
        if (this.accessObj[menu.text]) {
          if (this.authService.proxyLogin) {
            this.menuArray.push(menu)
          } else {
            if (menu?.children && menu.children.length > 0) {
              menu.children = menu.children.filter((el: any) => this.authService.permissions[el.view])
            }
            if (menu.view && !this.authService.permissions[menu.view] && !this.openAccessPages.includes(menu.text)) {
              continue;
            }

            this.menuArray.push(menu)

          }
        }
      }
    }

    // Update the component name after loading the navigation menu
    this.updateComponentName();
  }

  /**
   * Update the selected component name 
   * @returns returns nothing
   */
  updateComponentName() {
    if (this.router.url !== '/dashboard' && this.router.url !== '/') {
      this.guidedTourService.dashboardIndex = 0
    }
    for (let menu of this.menuArray) {
      this.componentName = '';
      if (menu.routerLink === this.location.path()) {
        this.componentName = menu.text;
        if (this.componentName.toLowerCase().includes('github')) {
          this.componentName = this.commonService.formatString(this.componentName)
        }
        return;
      } else if (menu?.children && menu.children.length > 0 && menu.children.find(el => el.routerLink === this.location.path())) {
        this.componentName = `${menu.text} / ${menu.children.find(el => el.routerLink === this.location.path())?.text as string}`;
        return;
      }
    }
    if (this.router.url.includes('/workflow-run')) {
      this.componentName = msg.wfRunCompName;
    }
    if (this.router.url.includes(GITHUB_WORKFLOWS_EXEC)) {
      this.componentName = msg.gitWfExecCompName;
    }
  }


  /**
   * accessUpdated is called inside ngOnInit and refers to the function present
   * in authService hasPagePermission which returns the features access list
   */
  async accessUpdated() {
    for (const obj in this.accessObj) {
      this.accessObj[obj] = this.authService.hasPagePermission(obj);
    }

    // Open-access pages are visible to every logged-in user, even without any role/page access.
    for (const page of this.openAccessPages) {
      this.accessObj[page] = true;
    }
    return;
  }

  // helpGuide will load the details of specific page based on the path
  /**
 * Loads the details of a specific page based on the current route path.
 * It determines the root path of the Markdown file based on the route and loads the corresponding content.
 */
  helpGuide() {
    let path = ''
    this.extLinks = this.extLinksService.links.filteredData;
    path = this.getUrlUsingSwitch();
    window.open(path, '_blank');
  }

  /**
   * By this function we are getting the repo names 
   */
  getWorkRepoNames(value?: string) {
    // this.repo = this.constants.WF_DEFAULT_OPT;
    this.wfQueueService.getQWorkflowName().subscribe((response: any) => {
      response = response?.data?.dbResp
      if (value == 'configUpdate') {
        this.repo = [];
        this.wfRunService.repoList([]);
        this.repo = this.repo.concat(response);  //concatting the API response with repo
        this.wfRunService.repoList(this.repo);
      } else {
        this.repo = this.repo.concat(response);  //concatting the API response with repo
        this.wfRunService.repoList(this.repo);
      }
    });
  }

  /**getPageNm function gets a list of all the pages which are present in access page */
  getPageNm() {
    this.appFeatService.getAllFeatures().subscribe((resp: any) => {
      this.pageNm = ['All', ...new Set(resp?.data?.dbResp.map((obj: { page: any; }) => this.commonService.formatString(obj.page)))];
    })
  }

  /**getGithubActions function is used to call the github action page api */
  getGithubActions() {
    this.configBuilderService.getAllWorkflows().subscribe((resp: any) => {
      this.gitActionsResp = this.commonService.transformJson(resp?.data?.dbResp);
      this.gitActionsResp = { "All": true, ...this.gitActionsResp }
    })
  }

  /**switch dropdown options to return different types of object for different pages */
  switchDropDownOptions(val?: any) {
    return '/admin'.includes(this.router.url) ? this.pageNm : this.repo;
  }

  /**
   * resetting the dropdown once changing the tabs 
   */
  resetDropdowns(val?: any) {
    this.repo = [...this.repo]; // Reset to the default value
    this.selectedRepo = this.constants.WF_DEFAULT_OPT[0]
    this.selectedDuration = this.durationDropdown[0];

    if (val === 'admin' && this.pageNm && this.pageNm.length > 0) {
      this.pageNm = [...this.pageNm]
      this.selectedRepo = this.pageNm[0];
      // Set the first option as selected
    }
  }

  /**
   * @param $event passing the event to trigger the dropdown update
   */
  updateDropDown($event: any) {
    this.selectedDuration = $event;
    this.commonService.dropDown($event, 'durationDropdown')
  }

  /**placeholder for app-autocomplete dropdown */
  placeholder() {
    return '/admin'.includes(this.router.url) ? 'Page name' : 'Repository'
  }

  /**ngOnDestroy */
  ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }


  /**
   * generic method to naviagte to the rout provided
   * @param url : url to navigate to specific rout
   */
  navigate(url: string) {
    this.router.navigate([`${url}`])
  }

  /**The getLinkUrl method retrieves the URL associated with a help item at a specified index, 
  or sets default link and tooltip values if no link is found */
  getLinkUrl(index: number | null) {
    if (index == null) {
      this.helpButtonLink = true;
      this.matTooltipHelp = this.noLinkMsg;
      return;
    }
    const linkData = this.extLinks.filter((link: any) => link.extLinkLabel.trim().toLowerCase() === this.helpItem[index].label.trim().toLowerCase())[0];
    if (linkData && linkData.isActive) {
      this.helpButtonLink = false;
      this.matTooltipHelp = ""
      return linkData.url;
    }
    else {
      this.helpButtonLink = true;
      this.matTooltipHelp = this.noLinkMsg;
      return linkData;
    }

  }

  /*This is a navigation bar level method. Upon user selection of the help option, the help page navigation is dynamically updated based on the specific link's associated data. */
  onClickHelp() {
    let path = ''
    this.extLinks = this.extLinksService.links.filteredData;
    path = this.getUrlUsingSwitch();
  }

  /* Returns url of help page or null based on the links data */
  getUrlUsingSwitch() {
    let path = ''
    const currentUrl = this.router.url.split('/')[1];
    switch (currentUrl) {
      case 'admin': path = this.getLinkUrl(5); break;
      case 'dashboard': path = this.getLinkUrl(0); break;
      case 'feature-workflow': path = this.getLinkUrl(1); break;
      case 'queue-mngr': path = this.getLinkUrl(3); break;
      case 'workflow-config': path = this.getLinkUrl(2); break;
      case 'integrated-workflows': path = path + ''; break;
      case 'workflows': path = this.getLinkUrl(7); break;
      case 'workflow-run': path = this.getLinkUrl(5); break;
      case 'tools': path = this.getLinkUrl(6); break;
      case 'github-actions': path = this.getLinkUrl(4); break;
      case 'links': path = this.getLinkUrl(8); break;
    }
    return path;
  }

  /**API call for fetching all data */
  getExternalLinksData(): void {
    this.extLinksService.getExternalLinksData().subscribe(
      async (response: any) => {
        // Map function to assign label instead of value for extLinkLabel
        const links = response.data?.dbResp?.map((item: any) => {
          const option = this.options.find((opt) => opt.value === item.extLinkLabel);
          if (option) {
            item.extLinkLabel = option.label;
          }
          return item; // Return the modified item
        });

        // Map function to add iconUrl in the existing response
        const addIconInLinks = await Promise.all(links.map(async (item: any) => {
          const newItem = { ...item };

          if (newItem?.iconId) {
            const iconResp: any = await this.extLinksService.getImage(newItem.iconId).toPromise().catch((err: HttpErrorResponse) => {
              console.error(`Error in getImage API Call:`, err)
              return null;
            });
            let iconUrl = iconResp?.data?.dbResp
            newItem.iconUrl = iconUrl; // Append the icon data to the original item
          }

          return newItem;
        }));

        this.extLinksService.linksWithIcons = addIconInLinks;
        this.extLinksService.links = new MatTableDataSource<any>(addIconInLinks);

        this.extLinksService.linksEvent.emit(true)
        this.getDashboardUrl();
      },
      () => {
        this.ts.showError(msg.failed, msg.externalLinksMsgs.fetchAllLinksFail);
      }
    );
  }

  getDashboardUrl() {
    const extLinks = this.extLinksService.links?.filteredData;
    let data = extLinks.find((link: any) => link.extLinkLabel === this.cicdLabel);

    if (data && data.isActive) {
      this.commonService.cicd_dashboard = data
    }
  }

}